import React from 'react';

import { Avatar, Text } from '@change/ds-core-ui';
import { CheckCircleIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useEndorsementsSectionList } from './hooks/useEndorsementsSectionList';
import type { Endorsement, VoterGuidePick } from './hooks/useEndorsementsSectionList';

export const EndorsementsSectionName: React.FC<{
	endorsement: Endorsement | VoterGuidePick;
	type: 'endorsement' | 'voterGuidePick';
	// eslint-disable-next-line complexity
}> = ({ endorsement, type }) => {
	let name;
	let photoUrl;

	if (type === 'endorsement') {
		const { person, organization } = endorsement as Endorsement;
		name = person?.displayName ?? organization?.name ?? 'Endorser';
		photoUrl = person?.profilePhoto?.photoUrl ?? organization?.profilePhoto?.photoUrl;
	} else {
		const { voterGuide } = endorsement as VoterGuidePick;
		name = voterGuide.author.displayName;
		photoUrl = voterGuide.author.profilePhoto?.photoUrl;
	}

	return (
		<div className="flex flex-row items-center gap-2">
			<div className="relative">
				<Avatar
					src={photoUrl ?? undefined}
					alt={name}
					size="small"
					style={{ boxShadow: '0 0 0 1px var(--color-stroke-weaker)' }}
					insetBorder
				/>
				<CheckCircleIcon aria-hidden className="text-icon-success absolute -right-1 bottom-0 z-10 size-3" />
			</div>
			<Text variant="body" size="small" className="text-typography-strong">
				{name}
			</Text>
		</div>
	);
};

export const EndorsementsSectionList: React.FC = () => {
	const {
		data: { voterGuidePicks, endorsements, totalRecommendations },
	} = useEndorsementsSectionList();
	if (totalRecommendations === 0) return null;

	const isSingleColumn = totalRecommendations <= 6;

	return (
		<div data-testid="dm-endorsements-section-list-container">
			<div className="my-6">
				<Text variant="body" size="small" className="text-typography-strong pb-4" fontWeight="bold">
					{totalRecommendations} Recommendation{totalRecommendations !== 1 ? 's' : ''}
				</Text>
			</div>
			<div className={cn('grid grid-cols-1 gap-4', !isSingleColumn && 'md:grid-cols-2')}>
				{voterGuidePicks?.map((pick) => (
					<EndorsementsSectionName key={`PickEndorsementName_${pick.id}`} endorsement={pick} type="voterGuidePick" />
				))}
				{endorsements?.map((endorsement) => (
					<EndorsementsSectionName
						key={`EndorsementName_${endorsement.id}`}
						endorsement={endorsement}
						type="endorsement"
					/>
				))}
			</div>
		</div>
	);
};
